import ArrowUpDown from 'lucide-react-native/icons/arrow-up-down';
import Check from 'lucide-react-native/icons/check';
import ChevronDown from 'lucide-react-native/icons/chevron-down';
import RotateCcw from 'lucide-react-native/icons/rotate-ccw';
import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import {
  BOAT_PRICES,
  BOAT_SORTS,
  BOAT_TYPES,
  DEFAULT_BOAT_FILTERS,
  DEFAULT_EVENT_FILTERS,
  DEFAULT_TOUR_FILTERS,
  EVENT_PRICES,
  EVENT_SORTS,
  EVENT_WHENS,
  TOUR_DURATIONS,
  TOUR_FORMATS,
  TOUR_PRICES,
  TOUR_SORTS,
  type BoatFilters,
  type EventFilters,
  type TourFilters,
} from '@todo/core/hub-filters';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type HubFilterGroup = {
  id: string;
  /** The pill's caption while the group is at its default. */
  label: string;
  value: string;
  defaultValue: string;
  /** `short`, when set, is the pill's caption for that option (the list shows `label`). */
  options: { value: string; label: string; short?: string }[];
  /** A sort, not a filter: the pill always names the current choice. */
  sort?: boolean;
};

// All prices are EUR; same "€120" format as the cards.
function eur(value: number): string {
  return `€${value}`;
}

function priceOptions(prices: Record<string, { min?: number; max?: number }>, t: Translate) {
  return Object.entries(prices).map(([value, { min, max }]) => ({
    value,
    label:
      min == null && max == null
        ? t('hubFilters.priceAny')
        : min == null
          ? t('hubFilters.priceUnder', { price: eur(max!) })
          : max == null
            ? t('hubFilters.priceOver', { price: eur(min) })
            : t('hubFilters.priceBetween', { min: eur(min), max: eur(max) }),
  }));
}

/** The /tours hub's groups, in display order. */
export function tourFilterGroups(filters: TourFilters, t: Translate): HubFilterGroup[] {
  return [
    {
      id: 'duration',
      label: t('hubFilters.duration'),
      value: filters.duration,
      defaultValue: DEFAULT_TOUR_FILTERS.duration,
      options: Object.keys(TOUR_DURATIONS).map((value) => ({
        value,
        label: t(`hubFilters.durationOptions.${value}`),
      })),
    },
    {
      id: 'format',
      label: t('hubFilters.format'),
      value: filters.format,
      defaultValue: DEFAULT_TOUR_FILTERS.format,
      options: TOUR_FORMATS.map((value) => ({
        value,
        label: t(`hubFilters.formatOptions.${value}`),
      })),
    },
    {
      id: 'price',
      label: t('hubFilters.price'),
      value: filters.price,
      defaultValue: DEFAULT_TOUR_FILTERS.price,
      options: priceOptions(TOUR_PRICES, t),
    },
    {
      id: 'sort',
      label: t('hubFilters.sort'),
      value: filters.sort,
      defaultValue: DEFAULT_TOUR_FILTERS.sort,
      options: TOUR_SORTS.map((value) => ({ value, label: t(`hubFilters.sortOptions.${value}`) })),
      sort: true,
    },
  ];
}

/** The /boats hub's groups, in display order. */
export function boatFilterGroups(filters: BoatFilters, t: Translate): HubFilterGroup[] {
  return [
    {
      id: 'type',
      label: t('hubFilters.boatType'),
      value: filters.type,
      defaultValue: DEFAULT_BOAT_FILTERS.type,
      options: Object.keys(BOAT_TYPES).map((value) => ({
        value,
        label: t(`hubFilters.boatTypeOptions.${value}`),
      })),
    },
    {
      id: 'price',
      label: t('hubFilters.pricePerDay'),
      value: filters.price,
      defaultValue: DEFAULT_BOAT_FILTERS.price,
      options: priceOptions(BOAT_PRICES, t),
    },
    {
      id: 'sort',
      label: t('hubFilters.sort'),
      value: filters.sort,
      defaultValue: DEFAULT_BOAT_FILTERS.sort,
      options: BOAT_SORTS.map((value) => ({
        value,
        label: t(`hubFilters.sortOptions.${value}`),
        // The full "Build year: newest first" overflows a phone-width pill.
        ...(value === 'newest' ? { short: t('hubFilters.sortBuildYear') } : {}),
      })),
      sort: true,
    },
  ];
}

/**
 * The /events hub's and /[city]/events pages' groups, in display order.
 * `categoryIds` are the categories worth offering (each page passes the ones
 * it has shows in), in `categories` order.
 */
export function eventFilterGroups(
  filters: EventFilters,
  t: Translate,
  categoryIds: string[],
): HubFilterGroup[] {
  return [
    {
      id: 'when',
      label: t('hubFilters.when'),
      value: filters.when,
      defaultValue: DEFAULT_EVENT_FILTERS.when,
      options: EVENT_WHENS.map((value) => ({ value, label: t(`hubFilters.whenOptions.${value}`) })),
    },
    {
      id: 'category',
      label: t('hubFilters.category'),
      value: filters.category,
      defaultValue: DEFAULT_EVENT_FILTERS.category,
      options: [
        { value: 'all', label: t('common.allCategories') },
        ...categoryIds.map((id) => ({ value: id, label: t(`categories.${id}`) })),
      ],
    },
    {
      id: 'price',
      label: t('hubFilters.price'),
      value: filters.price,
      defaultValue: DEFAULT_EVENT_FILTERS.price,
      options: priceOptions(EVENT_PRICES, t),
    },
    {
      id: 'sort',
      label: t('hubFilters.sort'),
      value: filters.sort,
      defaultValue: DEFAULT_EVENT_FILTERS.sort,
      options: EVENT_SORTS.map((value) => ({ value, label: t(`hubFilters.sortOptions.${value}`) })),
      sort: true,
    },
  ];
}

/**
 * The hubs' and city listing pages' filter row: one pill per group in a single
 * horizontal row (so the cards' first row — the LCP — stays near the top),
 * and the options of the open pill as chips beneath it. Picking an option
 * closes it. Pure UI: the hub owns the values (useFilteredHub).
 */
export function HubFilters({
  groups,
  onChange,
  onReset,
}: {
  groups: HubFilterGroup[];
  onChange: (groupId: string, value: string) => void;
  onReset: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const open = groups.find((group) => group.id === openId);
  const anySet = groups.some((group) => group.value !== group.defaultValue);

  return (
    <View style={styles.root}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}>
        {groups.map((group) => {
          const set = group.value !== group.defaultValue;
          const current = group.options.find((option) => option.value === group.value);
          const caption =
            group.sort || set ? (current?.short ?? current?.label ?? group.label) : group.label;
          const expanded = group.id === openId;
          const fg = set ? theme.accentForeground : theme.text;
          return (
            <Pressable
              key={group.id}
              accessibilityRole="button"
              aria-expanded={expanded}
              accessibilityLabel={group.sort ? `${group.label}: ${current?.label ?? caption}` : caption}
              onPress={() => setOpenId(expanded ? null : group.id)}>
              {({ pressed }) => (
                <View
                  style={[
                    styles.pill,
                    {
                      backgroundColor: set ? theme.accent : theme.card,
                      borderColor: set || expanded ? theme.gold : theme.border,
                    },
                    pressed && { opacity: 0.85 },
                  ]}>
                  {group.sort ? <ArrowUpDown size={14} color={theme.mutedForeground} /> : null}
                  <Text style={[styles.pillText, { color: fg }]}>{caption}</Text>
                  <ChevronDown
                    size={14}
                    color={set ? theme.accentForeground : theme.mutedForeground}
                    style={expanded ? styles.flipped : undefined}
                  />
                </View>
              )}
            </Pressable>
          );
        })}
        {anySet ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              setOpenId(null);
              onReset();
            }}>
            {({ pressed }) => (
              <View style={[styles.reset, pressed && { opacity: 0.6 }]}>
                <RotateCcw size={14} color={theme.mutedForeground} />
                <Text style={[styles.resetText, { color: theme.mutedForeground }]}>
                  {t('hubFilters.reset')}
                </Text>
              </View>
            )}
          </Pressable>
        ) : null}
      </ScrollView>

      {open ? (
        <View style={styles.options}>
          {open.options.map((option) => {
            const selected = option.value === open.value;
            return (
              <Pressable
                key={option.value}
                accessibilityRole="button"
                aria-pressed={selected}
                onPress={() => {
                  setOpenId(null);
                  if (!selected) onChange(open.id, option.value);
                }}>
                {({ pressed }) => (
                  <View
                    style={[
                      styles.option,
                      {
                        backgroundColor: selected ? theme.accent : theme.card,
                        borderColor: selected ? theme.accent : theme.border,
                      },
                      pressed && { opacity: 0.85 },
                    ]}>
                    {selected ? <Check size={13} color={theme.accentForeground} /> : null}
                    <Text
                      style={[
                        styles.optionText,
                        { color: selected ? theme.accentForeground : theme.text },
                      ]}>
                      {option.label}
                    </Text>
                  </View>
                )}
              </Pressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

/**
 * What a filtered hub says above its cards: the number of matches, "nothing
 * matches", or a failed fetch with a retry; "Loading…" (never the old count)
 * while a new result is on its way. Nothing while unfiltered.
 */
export function HubFilterStatus({
  filtered,
  total,
  loading,
  error,
  countLabel,
  onRetry,
  onReset,
}: {
  filtered: boolean;
  total: number | null;
  loading: boolean;
  error: boolean;
  countLabel: (count: number) => string;
  onRetry: () => void;
  onReset: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  if (error) {
    return (
      <View style={styles.status}>
        <Text style={[styles.statusText, { color: theme.destructive }]}>
          {t('hubFilters.loadError')}
        </Text>
        <StatusLink label={t('hubFilters.retry')} onPress={onRetry} />
      </View>
    );
  }
  if (loading) {
    return (
      <View style={styles.status}>
        <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
          {t('common.loadingMore')}
        </Text>
      </View>
    );
  }
  if (!filtered || total == null) return null;
  if (total === 0) {
    return (
      <View style={styles.status}>
        <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
          {t('hubFilters.noResults')}
        </Text>
        <StatusLink label={t('hubFilters.reset')} onPress={onReset} />
      </View>
    );
  }
  return (
    <View style={styles.status}>
      <Text style={[styles.statusText, { color: theme.mutedForeground }]}>{countLabel(total)}</Text>
    </View>
  );
}

function StatusLink({ label, onPress }: { label: string; onPress: () => void }) {
  const theme = useTheme();
  return (
    <Pressable accessibilityRole="button" onPress={onPress}>
      {({ pressed }) => (
        <Text style={[styles.statusLink, { color: theme.gold }, pressed && { opacity: 0.6 }]}>
          {label}
        </Text>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: Spacing.two,
  },
  row: {
    gap: Spacing.one + 2,
    paddingRight: Spacing.three,
    paddingVertical: 2,
    alignItems: 'center',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1,
  },
  pillText: {
    fontSize: 13,
    fontWeight: '600',
  },
  flipped: {
    transform: [{ rotate: '180deg' }],
  },
  reset: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.two,
    paddingVertical: 7,
  },
  resetText: {
    fontSize: 13,
  },
  options: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one + 2,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
  },
  optionText: {
    fontSize: 13,
    fontWeight: '500',
  },
  status: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: Spacing.two,
  },
  statusText: {
    fontSize: 14,
  },
  statusLink: {
    fontSize: 14,
    fontWeight: '600',
  },
});
